import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { isSavage } from '../../../../shared/utils/brandNameHelper';
import { sizeByKey } from '../../../../shared/utils/sizeConstants';
import { FormattedMessage } from '../../../../techstyle-shared/react-intl';
import { useDomain } from '../../../../techstyle-shared/redux-core';
import { useProductFilters } from '../ProductFilterContext';
import { useProductFilters as useProductFiltersCustomFilters } from '../ProductFilterContextCustomFilters';
import ProductListingFilter from '../ProductListingFilter';
import { parseBraSize, parseBraSizes } from '../utils/sizes';

const BraSizeLabel = styled.span`
  display: flex;
  flex-direction: column;
`;

const BandSize = styled.span`
  font-size: 10px;
  opacity: 0.75;
  margin: -6px 0 2px 0;
`;

/**
 * A special version of `ProductListingFilter` with custom logic for separating
 * the `size_bra` aggregation filter into Band Size and Cup Size selections.
 */
const ProductSizeFilter = ({
  as: ElementType = ProductListingFilter,
  filterField: inputFilterField,
  selectedFilters: inputSelectedFilters,
  bandSizeCheckboxProps,
  cupSizeCheckboxProps,
  onChange,
  onFilterSizeClicked,
  ...rest
}) => {
  // These two contexts are identical for the purposes of what this
  // component does with them, but we need to check both to maintain
  // backwards compatibility.
  let filterContext = useProductFilters();
  const customFilterContext = useProductFiltersCustomFilters();
  const domain = useDomain();

  filterContext = filterContext || customFilterContext;

  if (typeof inputSelectedFilters === 'function') {
    if (filterContext) {
      inputSelectedFilters = inputSelectedFilters(
        filterContext.selectedFilters
      );
    } else {
      inputSelectedFilters = inputSelectedFilters();
    }
  } else if (!inputSelectedFilters) {
    inputSelectedFilters = filterContext.selectedFilters;
  }

  const inputBraFilterField = useMemo(() => {
    if (inputFilterField.field === 'size_bra') {
      return inputFilterField;
    } else if (inputFilterField.nestedFilters) {
      return inputFilterField.nestedFilters.find(
        (subFilter) => subFilter.field === 'size_bra'
      );
    }
  }, [inputFilterField]);

  const showBandAndCupBra = useMemo(() => {
    return isSavage() && inputBraFilterField;
  }, [inputBraFilterField]);

  const selectedCupSizesByBand = useMemo(
    () =>
      inputBraFilterField
        ? parseBraSizes({
            braSizes: inputSelectedFilters[inputBraFilterField.field],
          })
        : {},
    [inputBraFilterField, inputSelectedFilters]
  );

  const cupSizesByBand = useMemo(
    () =>
      inputBraFilterField
        ? parseBraSizes({
            braSizes: inputBraFilterField.items.map((item) => item.value),
          })
        : {},
    [inputBraFilterField]
  );

  const selectableBandSizes = useMemo(
    () =>
      Object.keys(cupSizesByBand).map((bandSize) => ({
        label: sizeByKey({
          domain,
          gender: 'F',
          category: 'band-size',
          key: bandSize,
        }),
        value: bandSize,
      })),
    [cupSizesByBand, domain]
  );

  const [lastSelectedBand, setSelectedBand] = useState(null);
  const activeBand = useMemo(() => {
    if (
      // If no explicit selection, stay in sync with selected and selectable
      // sizes.
      lastSelectedBand == null ||
      // If the last selected option is no longer selectable, prevent from
      // using stale value.
      !selectableBandSizes.some(
        (bandSize) => bandSize.value === lastSelectedBand
      )
    ) {
      let firstBand = Object.keys(selectedCupSizesByBand)[0];
      if (!firstBand && selectableBandSizes.length) {
        firstBand = selectableBandSizes[0].value;
      }
      return firstBand || null;
    }
    return lastSelectedBand;
  }, [lastSelectedBand, selectableBandSizes, selectedCupSizesByBand]);

  const selectableCupSizes = useMemo(
    () =>
      activeBand && cupSizesByBand[activeBand]
        ? cupSizesByBand[activeBand].map((cupSize) => ({
            label: (
              <BraSizeLabel>
                <BandSize>
                  {sizeByKey({
                    domain,
                    gender: 'F',
                    category: 'band-size',
                    key: activeBand,
                  })}
                </BandSize>
                <span>
                  {sizeByKey({
                    domain,
                    gender: 'F',
                    category: 'cup-size',
                    key: cupSize,
                  })}
                </span>
              </BraSizeLabel>
            ),
            value: cupSize,
          }))
        : [],
    [activeBand, cupSizesByBand, domain]
  );

  const filterField = useMemo(() => {
    if (inputBraFilterField && showBandAndCupBra) {
      const isTopLevel = inputFilterField === inputBraFilterField;
      const emptyMessage =
        inputBraFilterField.totalAggregationCount !== 0 ? (
          <FormattedMessage
            id="site_product_filter.select_band_size"
            defaultMessage="Select a band size."
          />
        ) : null;
      const braFields = [
        {
          label: isTopLevel ? 'Band Size' : 'Bra Band Size',
          field: 'size_band',
          filterCount: 0,
          items: selectableBandSizes,
          totalAggregationCount: inputBraFilterField.totalAggregationCount,
          bandSizeCheckboxProps,
        },
        {
          label: isTopLevel ? 'Cup Size' : 'Bra Cup Size',
          field: 'size_cup',
          filterCount: inputSelectedFilters[inputBraFilterField.field].length,
          items: selectableCupSizes,
          totalAggregationCount: inputBraFilterField.totalAggregationCount,
          emptyMessage: emptyMessage,
          cupSizeCheckboxProps,
        },
      ];
      if (isTopLevel) {
        return {
          ...inputFilterField,
          nestedFilters: braFields,
          items: null,
        };
      } else {
        const nestedFilters = inputFilterField.nestedFilters.slice();
        // Replace `size_bra` with these filters.
        const braIndex = nestedFilters.indexOf(inputBraFilterField);
        nestedFilters.splice(braIndex, 1, ...braFields);
        return { ...inputFilterField, nestedFilters };
      }
    } else {
      return inputFilterField;
    }
  }, [
    inputBraFilterField,
    showBandAndCupBra,
    inputFilterField,
    selectableBandSizes,
    bandSizeCheckboxProps,
    inputSelectedFilters,
    selectableCupSizes,
    cupSizeCheckboxProps,
  ]);

  const selectedFilters = useMemo(
    () =>
      inputBraFilterField
        ? {
            ...inputSelectedFilters,
            size_band: activeBand,
            size_cup: activeBand
              ? selectedCupSizesByBand[activeBand] || []
              : [],
          }
        : inputSelectedFilters,
    [
      activeBand,
      inputBraFilterField,
      inputSelectedFilters,
      selectedCupSizesByBand,
    ]
  );

  const selectedFieldFilters = selectedFilters[filterField.field] ?? [];
  const [lastSelectedItem] = selectedFieldFilters.slice(-1);
  const prevLastSelectedItem = useRef(lastSelectedItem);
  const isLastSelectedItemChanging =
    lastSelectedItem !== prevLastSelectedItem.current;

  useEffect(() => {
    if (isLastSelectedItemChanging) {
      if (inputFilterField.field === 'size_bra') {
        if (lastSelectedItem) {
          const [bandSize] = parseBraSize(lastSelectedItem);

          if (bandSize !== lastSelectedBand) {
            setSelectedBand(bandSize);
          }
        } else {
          setSelectedBand(null);
        }
      }
      prevLastSelectedItem.current = lastSelectedItem;
    }
  }, [
    isLastSelectedItemChanging,
    inputFilterField.field,
    lastSelectedBand,
    lastSelectedItem,
  ]);

  const handleFilterChange = useCallback(
    ({
      filterField,
      newSelectedFilters,
      newSelectedFilterValue,
      newAllSelectedFilters,
      event,
      ...rest
    }) => {
      if (filterField.field === 'size_band' && showBandAndCupBra) {
        setSelectedBand(newSelectedFilters);
      } else if (filterField.field === 'size_cup' && showBandAndCupBra) {
        const newBraSizes = {
          ...selectedCupSizesByBand,
          [activeBand]: newSelectedFilters,
        };
        // Convert back into a flat size list like the parent expects.
        const newSizeArray = [];
        for (const bandSize in newBraSizes) {
          for (const cupSize of newBraSizes[bandSize]) {
            newSizeArray.push(`${bandSize}${cupSize}`);
          }
        }
        const updatedNewSizesFilters = {
          ...newAllSelectedFilters,
          size_bra:
            event.target.type === 'radio'
              ? `${activeBand}${newSelectedFilterValue}`
              : newSizeArray,
        };
        onFilterSizeClicked &&
          onFilterSizeClicked({
            newSelectedFilterValue,
            newAllSelectedFilters: updatedNewSizesFilters,
          });
        onChange({
          filterField: inputBraFilterField,
          newSelectedFilters: newSizeArray,
          ...rest,
        });
      } else {
        onFilterSizeClicked &&
          onFilterSizeClicked({
            newSelectedFilterValue,
            newAllSelectedFilters,
          });

        onChange({
          filterField,
          newSelectedFilters:
            event.target.type === 'radio'
              ? [newSelectedFilterValue]
              : newSelectedFilters,
          newSelectedFilterValue,
          ...rest,
        });
      }
    },
    [
      activeBand,
      showBandAndCupBra,
      inputBraFilterField,
      onChange,
      onFilterSizeClicked,
      selectedCupSizesByBand,
    ]
  );

  return (
    <ElementType
      filterField={filterField}
      selectedFilters={selectedFilters}
      onChange={handleFilterChange}
      {...rest}
    />
  );
};

ProductSizeFilter.propTypes = {
  as: PropTypes.elementType,
  bandSizeCheckboxProps: PropTypes.object,
  cupSizeCheckboxProps: PropTypes.object,
  filterField: PropTypes.object,
  onChange: PropTypes.func,
  onFilterSizeClicked: PropTypes.func,
  selectedFilters: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
};

ProductSizeFilter.BandSize = BandSize;

export default ProductSizeFilter;
